import {
  type HighlightsController,
  type HighlightsRenderer,
} from '@databases/highlights';
import {
  HIGHLIGHT_ACTIVE_CLASS,
  HIGHLIGHT_FILL_HANDLE_CLASS,
} from '@databases/highlights/constants';

import { createEffect } from '../utils/create-effect';

import { FILL_HANDLE_LAYER_Z_INDEX, HIGHLIGHTS_LAYER_Z_INDEX } from './constants';
import {
  createResizeListener,
  RESIZE_OBSERVER_ELEMENT_ID,
} from './utils/create-resize-listener';
import { syncRangeToHighlightsLayer } from './utils/sync-range-to-highlights-layer';

/**
 * Creates the main effect that draws / renders selection highlights
 * (the blue backgrounds) and the fill handle (the little blue circle).
 */
export const createHighlightsEffect = createEffect((controller: HighlightsController) => {
  // ---------------------------------------------------------------------------
  // DOM
  // ---------------------------------------------------------------------------
  let scrollableContainer: HTMLElement | null = null;

  // ---------------------------------------------------------------------------
  // Renderers
  // ---------------------------------------------------------------------------
  let highlightsRenderer: HighlightsRenderer | null = null;
  let fillHandleLayer: HighlightsRenderer | null = null;

  let resizeObserverCleanup: (() => void) | null = null;

  function ensureDependentElementsAndRenderer() {
    controller.mount();

    if (!scrollableContainer) {
      scrollableContainer = controller.getScrollContainer();
    }

    if (!highlightsRenderer && scrollableContainer) {
      highlightsRenderer = controller.createLayer(HIGHLIGHTS_LAYER_Z_INDEX);
    }

    if (!fillHandleLayer && scrollableContainer) {
      fillHandleLayer = controller.createLayer(FILL_HANDLE_LAYER_Z_INDEX);
    }

    if (!resizeObserverCleanup && scrollableContainer) {
      const resizeElement = document.getElementById(RESIZE_OBSERVER_ELEMENT_ID);

      if (resizeElement) {
        resizeObserverCleanup = createResizeListener(resizeElement, () => {
          highlightsRenderer?.redrawAll();
          fillHandleLayer?.redrawAll();
        });
      }
    }

    return !!(highlightsRenderer && scrollableContainer && fillHandleLayer);
  }

  return {
    deltaTypes: [
      'selectionModified',
      'selectionReplaced',
      'selectionPreview',
      'cellEditRequested',
      'cellEditStopped',
    ],

    dispose() {
      if (resizeObserverCleanup) {
        resizeObserverCleanup();

        resizeObserverCleanup = null;
      }

      if (highlightsRenderer) {
        controller.removeLayer(highlightsRenderer);

        highlightsRenderer = null;
      }

      if (fillHandleLayer) {
        controller.removeLayer(fillHandleLayer);

        fillHandleLayer = null;
      }

      if (scrollableContainer) {
        scrollableContainer.classList.remove(HIGHLIGHT_ACTIVE_CLASS);
      }

      scrollableContainer = null;
    },

    onDelta(delta, state) {
      if (
        !ensureDependentElementsAndRenderer() ||
        !highlightsRenderer ||
        !scrollableContainer ||
        !fillHandleLayer
      ) {
        return;
      }

      if (state.mode === 'idle' || state.selections.length === 0) {
        scrollableContainer.classList.remove(HIGHLIGHT_ACTIVE_CLASS);
      } else {
        scrollableContainer.classList.add(HIGHLIGHT_ACTIVE_CLASS);
      }

      if (delta.kind === 'selectionPreview') {
        syncRangeToHighlightsLayer(highlightsRenderer, state.mode, delta.activeRange);

        // we don't want to show this when there is a selection preview
        fillHandleLayer.clear();

        return;
      }

      if (delta.kind === 'cellEditRequested') {
        highlightsRenderer.hideAll();
        fillHandleLayer.hideAll();

        return;
      }

      if (delta.kind === 'cellEditStopped') {
        highlightsRenderer.showAll();
        fillHandleLayer.showAll();

        return;
      }

      // Handle Fill Handle placement
      if (state.selections.length > 0 && state.mode !== 'idle') {
        const activeSelection = state.selections[state.selections.length - 1];

        fillHandleLayer.clear();
        fillHandleLayer.add(
          {
            id: 'fill-handle-wrapper',
            anchor: {
              row: activeSelection.anchor.row.index,
              col: activeSelection.anchor.col.index,
            },
            lead: {
              row: activeSelection.lead.row.index,
              col: activeSelection.lead.col.index,
            },
          },
          state.mode,
          HIGHLIGHT_FILL_HANDLE_CLASS
        );
      } else {
        fillHandleLayer.clear();
      }

      // Handle Selections (The Blue Backgrounds)
      if (delta.kind === 'selectionReplaced') {
        highlightsRenderer.clear();

        for (const range of delta.selections) {
          syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
        }
        return;
      }

      if (delta.kind === 'selectionModified') {
        if (delta.rangeIdsToRemove) {
          for (const id of delta.rangeIdsToRemove) {
            highlightsRenderer.remove(id);
          }
        }

        if (delta.added) {
          for (const range of delta.added) {
            syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
          }
        }

        if (delta.updated) {
          for (const range of delta.updated) {
            syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
          }
        }
      }
    },
  };
});
